import type { TFunction } from 'i18next'

import {
  CDBalancePaymentMethod,
  CDBalancePaymentTime,
  CDBalanceTermsBeginOn,
  LoadboardMethod,
  LoadboardTerms,
} from '#/shared/api/generated/loadboard/models'
import type { SelectOption } from '#/shared/ui/select'

export function paymentMethodOptions(t: TFunction): SelectOption[] {
  return [
    { value: LoadboardMethod.cash, label: t('paymentOptions.method.cash') },
    {
      value: LoadboardMethod.cashiers_check,
      label: t('paymentOptions.method.cashCertifiedFunds'),
    },
    { value: LoadboardMethod.check, label: t('paymentOptions.method.check') },
    {
      value: LoadboardMethod.comchek,
      label: t('paymentOptions.method.comchek'),
    },
    { value: LoadboardMethod.ach, label: t('paymentOptions.method.ach') },
    {
      value: LoadboardMethod.credit_card,
      label: t('paymentOptions.method.creditCard'),
    },
    { value: LoadboardMethod.zelle, label: t('paymentOptions.method.zelle') },
    { value: LoadboardMethod.other, label: t('paymentOptions.method.other') },
  ]
}

export function paymentTermsOptions(t: TFunction): SelectOption[] {
  return [
    {
      value: LoadboardTerms.cash_on_delivery,
      label: t('paymentOptions.terms.delivery'),
    },
    {
      value: LoadboardTerms.cash_on_pickup,
      label: t('paymentOptions.terms.pickup'),
    },
  ]
}

export function balanceMethodOptions(t: TFunction): SelectOption[] {
  return [
    {
      value: CDBalancePaymentMethod.CASH,
      label: t('paymentOptions.balanceMethod.cash'),
    },
    {
      value: CDBalancePaymentMethod.CERTIFIED_FUNDS,
      label: t('paymentOptions.balanceMethod.certifiedFunds'),
    },
    {
      value: CDBalancePaymentMethod.COMPANY_CHECK,
      label: t('paymentOptions.balanceMethod.companyCheck'),
    },
    {
      value: CDBalancePaymentMethod.COMCHEK,
      label: t('paymentOptions.balanceMethod.comchek'),
    },
    {
      value: CDBalancePaymentMethod.TCH,
      label: t('paymentOptions.balanceMethod.tch'),
    },
  ]
}

export function balanceTimeOptions(t: TFunction): SelectOption[] {
  return [
    {
      value: CDBalancePaymentTime.IMMEDIATELY,
      label: t('paymentOptions.balanceTime.immediately'),
    },
    {
      value: CDBalancePaymentTime.TWO_BUSINESS_DAYS_QUICK_PAY,
      label: t('paymentOptions.balanceTime.twoBusinessDaysQuickPay'),
    },
    {
      value: CDBalancePaymentTime.FIVE_BUSINESS_DAYS,
      label: t('paymentOptions.balanceTime.fiveBusinessDays'),
    },
    {
      value: CDBalancePaymentTime.TEN_BUSINESS_DAYS,
      label: t('paymentOptions.balanceTime.tenBusinessDays'),
    },
    {
      value: CDBalancePaymentTime.FIFTEEN_BUSINESS_DAYS,
      label: t('paymentOptions.balanceTime.fifteenBusinessDays'),
    },
    {
      value: CDBalancePaymentTime.THIRTY_BUSINESS_DAYS,
      label: t('paymentOptions.balanceTime.thirtyBusinessDays'),
    },
  ]
}

export function balanceTermBeginsOptions(t: TFunction): SelectOption[] {
  return [
    {
      value: CDBalanceTermsBeginOn.PICKUP,
      label: t('paymentOptions.balanceTermBegins.pickup'),
    },
    {
      value: CDBalanceTermsBeginOn.DELIVERY,
      label: t('paymentOptions.balanceTermBegins.delivery'),
    },
    {
      value: CDBalanceTermsBeginOn.RECEIVING_SIGNED_BOL,
      label: t('paymentOptions.balanceTermBegins.receivingSignedBol'),
    },
  ]
}

export const DEFAULT_BALANCE_SELECTION = {
  balanceMethod: CDBalancePaymentMethod.CASH,
  balanceTime: CDBalancePaymentTime.IMMEDIATELY,
  balanceTermBegins: CDBalanceTermsBeginOn.PICKUP,
} as const

function findLabel(
  options: SelectOption[],
  value: string | null | undefined,
): string | undefined {
  if (!value) return undefined
  return options.find((option) => option.value === value)?.label
}

export function paymentOptionLabels(t: TFunction) {
  const method = paymentMethodOptions(t)
  const terms = paymentTermsOptions(t)
  const balanceMethod = balanceMethodOptions(t)
  const balanceTime = balanceTimeOptions(t)
  const balanceTermBegins = balanceTermBeginsOptions(t)
  return {
    method: (value: string | null | undefined) => findLabel(method, value),
    terms: (value: string | null | undefined) => findLabel(terms, value),
    balanceMethod: (value: string | null | undefined) =>
      findLabel(balanceMethod, value),
    balanceTime: (value: string | null | undefined) =>
      findLabel(balanceTime, value),
    balanceTermBegins: (value: string | null | undefined) =>
      findLabel(balanceTermBegins, value),
  }
}
